import React from 'react';
import { ActivityIndicator, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { avatarDefault } from '@/assets/images';
import { Divider, Scroll, Stack } from '@/components/layout';
import { Image, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

const strings = STRINGS.guardianPlayer.leaveCircle;
const AVATAR_SIZE = 60;

export type LeaveCircleContentProps = {
  screenTitle: string;
  /** Player first name, woven into the warning + "you'll lose" copy. */
  playerName: string;
  avatarUrl?: string;
  /** Card name line, e.g. "Church Benjamin". */
  fullName: string;
  /** Blue detail line, e.g. "Age 8 · Level 3". */
  detailLine: string;
  /** Muted membership line, e.g. "Since Sep 2023". */
  sinceLine?: string;
  onBack: () => void;
  onLeave: () => void;
  leaving?: boolean;
};

export default function LeaveCircleContent({
  screenTitle,
  playerName,
  avatarUrl,
  fullName,
  detailLine,
  sinceLine,
  onBack,
  onLeave,
  leaving = false,
}: LeaveCircleContentProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const { bottom } = useSafeAreaInsets();

  const staysItems = [strings.staysAccount, strings.staysPlayers];
  const loseItems = [
    strings.loseProgress.replace('{name}', playerName),
    strings.loseBookings.replace('{name}', playerName),
    strings.loseNotifications.replace('{name}', playerName),
  ];

  return (
    <Stack
      viewType="gradient"
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.91]}
      start={{ x: 1, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={styles.root}>
      <GuardianScreenHeader
        title={screenTitle}
        onBack={onBack}
        titleAlign="center"
        titleVariant="Font Guardian App/Body/Body 16 Bold"
        containerProps={isTablet ? undefined : { backgroundColor: 'transparent' }}
      />

      <Scroll
        flex={1}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          styles.content,
          { paddingBottom: Math.max(bottom + scale(12), scale(24)) },
        ]}>
        <Stack
          flex={1}
          width="100%"
          maxWidth={isTablet ? scale(380) : undefined}
          alignSelf="center"
          gap={scale(20)}>
          {/* Warning banner */}
          <Stack direction="row" align="flex-start" gap={scale(8)} style={styles.warning}>
            <SvgIcon name="IcWarning" size={scale(20)} color={guardianColors.Error} />
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
              style={styles.warningText}>
              {strings.warning.replace(/{name}/g, playerName)}
            </Text>
          </Stack>

          {/* Player + impact card */}
          <Stack style={styles.card} gap={scale(12)}>
            <Stack direction="row" align="center" gap={scale(12)}>
              <Image
                source={avatarUrl ? { uri: avatarUrl } : avatarDefault}
                width={scale(AVATAR_SIZE)}
                height={scale(AVATAR_SIZE)}
                circular
                resizeMode="cover"
                defaultSource={avatarDefault}
              />
              <Stack flex={1} gap={scale(2)}>
                <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.name}>
                  {fullName}
                </Text>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                  style={styles.detail}>
                  {detailLine}
                </Text>
                {sinceLine ? (
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                    style={styles.since}>
                    {sinceLine}
                  </Text>
                ) : null}
              </Stack>
            </Stack>

            <Divider color={guardianColors['3']} />

            {/* Stays with you */}
            <Stack gap={scale(8)}>
              <Stack direction="row" align="center" gap={scale(6)}>
                <SvgIcon name="IcListCheck" size={scale(16)} color={guardianColors['Main/Main']} />
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 11 Semibold"
                  style={styles.sectionTitle}>
                  {strings.staysTitle}
                </Text>
              </Stack>
              {staysItems.map(item => (
                <Stack key={item} direction="row" align="center" gap={scale(8)}>
                  <SvgIcon name="IcCheckMd" size={scale(18)} color={guardianColors['10']} />
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                    style={styles.staysItem}>
                    {item}
                  </Text>
                </Stack>
              ))}
            </Stack>
            <Divider color={guardianColors['4']} />

            {/* You'll lose */}
            <Stack gap={scale(8)}>
              <Stack direction="row" align="center" gap={scale(6)}>
                <SvgIcon name="IcListCheck" size={scale(16)} color={guardianColors['Main/Main']} />
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 11 Semibold"
                  style={styles.sectionTitle}>
                  {strings.loseTitle}
                </Text>
              </Stack>
              {loseItems.map(item => (
                <Stack key={item} direction="row" align="flex-start" gap={scale(8)}>
                  <SvgIcon name="IcMinusCircle" size={scale(20)} color={guardianColors['5']} />
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                    style={styles.loseItem}>
                    {item}
                  </Text>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Scroll>

      {/* Leave */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={strings.leave}
        accessibilityState={{ busy: leaving, disabled: leaving }}
        disabled={leaving}
        onPress={onLeave}
        style={({ pressed }) => [styles.leaveButton, pressed ? styles.pressed : null]}>
        {leaving ? (
          <ActivityIndicator size="small" color={colors['Level 3']} />
        ) : (
          <>
            <SvgIcon name="IcLeave" size={scale(20)} color={colors['Level 3']} />
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
              style={styles.leaveLabel}>
              {strings.leave}
            </Text>
          </>
        )}
      </Pressable>
    </Stack>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => {
  const tabletLeaveButtonWidth = scale(380);

  return {
    root: {
      flex: 1,
    },
    content: {
      flexGrow: 1,
      paddingHorizontal: scale(20),
      paddingTop: scale(16),
      paddingBottom: scale(isTablet ? 108 : 100),
    },
    warning: {
      borderRadius: scale(12),
      borderCurve: 'continuous',
      paddingHorizontal: scale(8),
      paddingVertical: scale(12),
      borderWidth: 0.5,
      borderColor: colors['Level 3'],
      backgroundColor: withOpacity('#FF2D2D', 0.08),
    },
    warningText: {
      flex: 1,
      color: guardianColors['10'],
    },
    card: {
      backgroundColor: colors['0'],
      borderRadius: scale(12),
      borderCurve: 'continuous',
      paddingHorizontal: scale(12),
      paddingVertical: scale(12),
    },
    name: {
      color: guardianColors['10'],
    },
    detail: {
      color: guardianColors['Main/Main'],
    },
    since: {
      color: guardianColors['6'],
    },
    sectionTitle: {
      color: guardianColors['6'],
    },
    staysItem: {
      flex: 1,
      color: guardianColors['10'],
    },
    loseItem: {
      flex: 1,
      color: guardianColors['5'],
    },
    leaveButton: {
      position: 'absolute',
      bottom: scale(isTablet ? 40 : 32),
      ...(isTablet
        ? {
            left: '50%',
            marginLeft: -tabletLeaveButtonWidth / 2,
            width: tabletLeaveButtonWidth,
          }
        : {
            left: scale(20),
            right: scale(20),
          }),
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: scale(8),
      height: scale(48),
      borderRadius: scale(12),
      borderCurve: 'continuous',
      borderWidth: 1,
      borderColor: colors['Level 3'],
      backgroundColor: colors['0'],
    },
    leaveLabel: {
      color: colors['Level 3'],
    },
    pressed: {
      opacity: 0.7,
    },
  };
});
